import { Link } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useMatch } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useScrolled } from "../utils/Gsap/useScrolled";
import { getLandingPageData } from "../utils/api/helpers/pages";
import { useActiveGenre } from "../utils/Genre/activeGenreState";
// import {
//   setDisplayScreenerLogin,
//   setDisplayScreenerProjectList,
//   useScreenerLoggedIn,
//   useIndeeApiChecked,
// } from "../utils/Screeners/useScreeners";
import SearchShows from "./SearchShows";
import { stopAllPageMedia } from "../utils/Actions/stopAllPageMedia";

export default function Header() {
  // const screenerLoggedIn = useScreenerLoggedIn();
  // const indeeApiChecked = useIndeeApiChecked();

  const { genreSlug, showSlug, sectionSlug, mediaSlug, eventSlug } =
    useParams();
  const isAwardsRoute = useMatch("/:genreSlug/:showSlug/awards");
  const [displaySearch, setDisplaySearch] = useState(false);

  const [allGenres, setAllGenres] = useState(false); // your genre dropdown
  const [menuOpen, setMenuOpen] = useState(false); // your genre dropdown
  const [activeGenre, setActiveGenre] = useActiveGenre();

  // NEW: mobile flyout state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  // NEW: refs for accessibility
  const mobilePanelRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);

  const scrolled = useScrolled(75);

  const loadingTransition = genreSlug && activeGenre?.slug !== genreSlug;

  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    getLandingPageData().then((result) => {
      setAllGenres(result.data.GenreList);
      const matchedGenre = result.data.GenreList.find(
        (genre) => genre.slug === genreSlug,
      );
      if (matchedGenre) {
        setActiveGenre(matchedGenre);
      }
    });
  }, [genreSlug]);

  // Close genre menu on click outside or ESC (existing)
  useEffect(() => {
    function handleClickOutside(event) {
      if (navRef.current && !navRef.current.contains(event.target)) {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
        setMenuOpen(false);
      }
    }

    function handleEsc(event) {
      if (event.key === "Escape") {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
        setMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const handleLinkClick = (idx) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);

    closeTimeoutRef.current = setTimeout(() => {
      setMenuOpen(false);
      closeTimeoutRef.current = null;
    }, 250);
  };

  // NEW: close flyout on ESC + focus trap + body scroll lock
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const previousActive = document.activeElement;
    const bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus the panel (or first focusable item)
    setTimeout(() => {
      const panel = mobilePanelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length) focusables[0].focus();
      else panel.focus();
    }, 0);

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        return;
      }
      if (e.key !== "Tab") return;

      const panel = mobilePanelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = bodyOverflow;
      document.removeEventListener("keydown", onKeyDown);

      // restore focus to the menu button
      if (previousActive && previousActive.focus) previousActive.focus();
      if (mobileMenuButtonRef.current) mobileMenuButtonRef.current.focus();
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header
      className={
        "page-header with-background " + (scrolled && "with-background")
      }
    >
      <div className="page-header-inner">
        <div className="page-identity">
          <Link to="/" className="">
            <img
              src="/img/netflix-logo-ribbon.png"
              alt="NF"
              className="page-logo"
            />
          </Link>
          <Link to="/" className="page-identity-copy">
            <div className="page-identity-title">Series</div>
            <div className="page-identity-subtitle d-none d-sm-block">
              For Your Consideration
            </div>
            <div className="page-identity-subtitle d-block d-sm-none">FYC</div>
          </Link>
        </div>

        {/* Drop-down nav */}
        {genreSlug && !showSlug && activeGenre && (
          <div
            className={"genre-nav-container"}
            style={
              loadingTransition ? { pointerEvents: "none", opacity: 0 } : {}
            }
          >
            <nav className="genre-nav" aria-label="Series Genres" ref={navRef}>
              <button
                aria-haspopup="true"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(!menuOpen)}
                className={`genre-nav-toggle ${menuOpen ? "open" : ""}`}
              >
                <span>{activeGenre?.title}</span>
                <span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={"genre-nav-icon"}
                  >
                    <path
                      d="M20 8L12 16L4 8"
                      style={{
                        stroke: activeGenre?.theme?.colors?.light?.hex,
                      }}
                      strokeWidth="2"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                </span>
              </button>
              <ul className={`genre-nav-list ${menuOpen ? "open" : ""}`}>
                {allGenres &&
                  allGenres.map((genre, idx) => (
                    <li
                      key={genre.slug}
                      style={
                        genre.slug === genreSlug
                          ? { cursor: "default", pointerEvents: "none" }
                          : null
                      }
                    >
                      <Link
                        to={`/${genre.slug}`}
                        className={genre.slug === genreSlug ? "active" : ""}
                        onClick={() => handleLinkClick(idx)}
                      >
                        {genre.title}
                      </Link>
                    </li>
                  ))}
              </ul>
            </nav>
          </div>
        )}

        {genreSlug && showSlug && !mediaSlug && !isAwardsRoute && (
          <div className={"genre-nav-container"}>
            <Link to={"/" + genreSlug} className="genre-back-button">
              <span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={"genre-back-icon"}
                >
                  <g id="">
                    <path
                      strokeWidth="2"
                      strokeLinejoin="round"
                      d="M24,12H1 M1,12l7-7 M1,12l7,7"
                      style={{
                        stroke: activeGenre
                          ? activeGenre?.theme?.colors?.light?.hex
                          : "#fff",
                      }}
                    />
                  </g>
                  <g id="">
                    <polyline
                      className="st1"
                      points="8,5 1,12 8,19 	"
                      style={{
                        stroke: activeGenre
                          ? activeGenre?.theme?.colors?.light?.hex
                          : "#fff",
                      }}
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </span>
              <span className="genre-back-button-label">
                {activeGenre ? activeGenre?.title : "Genres"}
              </span>
            </Link>
          </div>
        )}

        {genreSlug && showSlug && !mediaSlug && isAwardsRoute && (
          <div className={"genre-nav-container"}>
            <Link
              to={"/" + genreSlug + "/" + showSlug}
              className="genre-back-button"
            >
              <span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={"genre-back-icon"}
                >
                  <g id="">
                    <path
                      strokeWidth="2"
                      strokeLinejoin="round"
                      d="M24,12H1 M1,12l7-7 M1,12l7,7"
                      style={{
                        stroke: activeGenre
                          ? activeGenre?.theme?.colors?.light?.hex
                          : "#fff",
                      }}
                    />
                  </g>
                  <g id="">
                    <polyline
                      className="st1"
                      points="8,5 1,12 8,19 	"
                      style={{
                        stroke: activeGenre
                          ? activeGenre?.theme?.colors?.light?.hex
                          : "#fff",
                      }}
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </span>
              <span className="genre-back-button-label">
                {showSlug && showSlug.replaceAll("-", " ")}{" "}
              </span>
            </Link>
          </div>
        )}

        {genreSlug && showSlug && sectionSlug && mediaSlug && (
          <div className={"genre-nav-container"}>
            <Link
              to={"/" + genreSlug + "/" + showSlug + "/" + sectionSlug}
              className="genre-back-button"
            >
              <span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={"genre-back-icon"}
                >
                  <g id="">
                    <path
                      strokeWidth="2"
                      strokeLinejoin="round"
                      d="M24,12H1 M1,12l7-7 M1,12l7,7"
                      style={{
                        stroke: activeGenre
                          ? activeGenre?.theme?.colors?.light?.hex
                          : "#fff",
                      }}
                    />
                  </g>
                  <g id="">
                    <polyline
                      className="st1"
                      points="8,5 1,12 8,19 	"
                      style={{
                        stroke: activeGenre
                          ? activeGenre?.theme?.colors?.light?.hex
                          : "#fff",
                      }}
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </span>
              <span className="genre-back-button-label">
                {showSlug && showSlug.replaceAll("-", " ")}{" "}
              </span>
            </Link>
          </div>
        )}

        {!genreSlug && !showSlug && !sectionSlug && !mediaSlug && eventSlug && (
          <div className={"genre-nav-container"}>
            <Link to={"/events"} className="genre-back-button">
              <span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={"genre-back-icon"}
                >
                  <g id="">
                    <path
                      strokeWidth="2"
                      strokeLinejoin="round"
                      d="M24,12H1 M1,12l7-7 M1,12l7,7"
                      style={{
                        stroke: "#fff",
                      }}
                    />
                  </g>
                  <g id="">
                    <polyline
                      className="st1"
                      points="8,5 1,12 8,19 	"
                      style={{
                        stroke: "#fff",
                      }}
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </span>
              <span className="genre-back-button-label">All Events</span>
            </Link>
          </div>
        )}

        {/* DESKTOP actions (hidden on mobile via CSS) */}
        <div
          className="page-header-buttons"
          // style={{
          //   marginLeft: !showSlug && !genreSlug && !mediaSlug && "auto",
          // }}
        >
          {/*<button*/}
          {/*  style={{*/}
          {/*    opacity: indeeApiChecked ? 1 : 0.3,*/}
          {/*    pointerEvents: indeeApiChecked ? "all" : "none",*/}
          {/*  }}*/}
          {/*  className={*/}
          {/*    "page-header-button screenerLink " +*/}
          {/*    (!indeeApiChecked ? "screenerLinkLoading" : "")*/}
          {/*  }*/}
          {/*  onClick={() => {*/}
          {/*    stopAllPageMedia();*/}
          {/*    if (screenerLoggedIn) {*/}
          {/*      setDisplayScreenerProjectList(true);*/}
          {/*    } else {*/}
          {/*      setDisplayScreenerLogin(true);*/}
          {/*    }*/}
          {/*  }}*/}
          {/*>*/}
          {/*  Screeners*/}
          {/*</button>*/}
          <Link to="/events" className="page-header-button">
            Events
          </Link>

          <button
            className="page-search"
            onClick={() => setDisplaySearch(true)}
          >
            <img
              src="/img/ico/search.png"
              alt="Search"
              className="page-search-icon"
            />
          </button>
        </div>

        {/* NEW: mobile menu icon button */}
        <button
          ref={mobileMenuButtonRef}
          className="mobile-menu-button ms-auto"
          type="button"
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-haspopup="dialog"
          aria-controls="mobile-flyout"
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((v) => !v)}
        >
          {/* simple “hamburger” icon */}
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M4 6h16M4 12h16M4 18h16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* NEW: overlay + flyout */}
        <div
          className={`mobile-flyout-overlay ${mobileMenuOpen ? "open" : ""}`}
          onClick={closeMobileMenu}
          aria-hidden={mobileMenuOpen ? "false" : "true"}
        >
          <aside
            id="mobile-flyout"
            className={`mobile-flyout ${mobileMenuOpen ? "open" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            ref={mobilePanelRef}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()} // prevent overlay close when clicking inside panel
          >
            <div className="mobile-flyout-header d-flex">
              {/*<div className="mobile-flyout-title">Menu</div>*/}
              <button
                className="mobile-flyout-close ms-auto"
                type="button"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M6 6l12 12M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div className="mobile-flyout-content">
              <div className="mobile-actions">
                {!isHome && (
                  <Link
                    to="/"
                    className="page-header-button"
                    onClick={closeMobileMenu}
                  >
                    Home
                  </Link>
                )}

                {/*<button*/}
                {/*  className="page-header-button"*/}
                {/*  type="button"*/}
                {/*  onClick={() => {*/}
                {/*    stopAllPageMedia();*/}
                {/*    if (screenerLoggedIn) {*/}
                {/*      setDisplayScreenerProjectList(true);*/}
                {/*    } else {*/}
                {/*      setDisplayScreenerLogin(true);*/}
                {/*    }*/}
                {/*    closeMobileMenu();*/}
                {/*  }}*/}
                {/*>*/}
                {/*  Screeners*/}
                {/*</button>*/}

                <Link
                  to="/events"
                  className="page-header-button"
                  onClick={closeMobileMenu}
                >
                  Events
                </Link>

                <button
                  className="page-header-button"
                  type="button"
                  onClick={() => {
                    setDisplaySearch(true);
                    closeMobileMenu();
                  }}
                >
                  Search
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
      <SearchShows
        displaySearch={displaySearch}
        setDisplaySearch={setDisplaySearch}
      />
    </header>
  );
}
